/**
 * useSOSAlerts - Real-time listener for SOS alerts
 *
 * Phase 4 hook. Subscribes to sos_alerts/ collection in Firestore
 * and returns active (unresolved) alerts. Used by tech dashboards
 * and office dashboards to show real-time emergency notifications.
 *
 * Filters by tenantId for multi-tenant safety.
 * Client-side filtering for resolvedAt — Firestore where('field', '==', null)
 * is unreliable for documents where the field is absent vs explicitly null.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { SOSAlert } from '@/types/schema';

interface UseSOSAlertsReturn {
  /** All active (unresolved) SOS alerts */
  alerts: SOSAlert[];
  /** Number of unacknowledged alerts */
  unacknowledgedCount: number;
  /** Whether the listener is still loading */
  loading: boolean;
}

/**
 * Subscribe to real-time SOS alerts for a tenant.
 *
 * @param tenantId - Current tenant ID (required for data isolation)
 * @returns Active alerts, unacknowledged count, and loading state
 */
export function useSOSAlerts(tenantId: string | null): UseSOSAlertsReturn {
  const [alerts, setAlerts] = useState<SOSAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    // Track both primary and fallback unsubscribes for proper cleanup
    let primaryUnsub: (() => void) | null = null;
    let fallbackUnsub: (() => void) | null = null;

    // Fetch most-recent alerts for tenant, filter resolved ones client-side.
    // Firestore where('resolvedAt', '==', null) doesn't reliably match
    // documents where the field is absent (only matches explicit null).
    // Capped at 100 most-recent: active alerts are always recent (techs hit
    // SOS in the moment), so we never miss an unresolved one this way; the
    // cap only truncates the old-resolved tail that was bleeding reads as
    // the collection grew over the years.
    const q = query(
      collection(db, 'sos_alerts'),
      where('tenantId', '==', tenantId),
      orderBy('triggeredAt', 'desc'),
      limit(100),
    );

    primaryUnsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as SOSAlert[];

        // Client-side filter for unresolved
        const active = data.filter((a) => !a.resolvedAt);
        setAlerts(active);
        setLoading(false);
      },
      (err) => {
        // Fallback: simpler query without ordering if index is missing
        console.error('[useSOSAlerts] Listener error:', err.message);

        // Primary failed — clean it up
        if (primaryUnsub) { primaryUnsub(); primaryUnsub = null; }

        const fallbackQ = query(
          collection(db, 'sos_alerts'),
          where('tenantId', '==', tenantId),
          limit(100),
        );

        fallbackUnsub = onSnapshot(fallbackQ, (snapshot) => {
          const data = snapshot.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
            })) as SOSAlert[];

          const active = data
            .filter((a) => !a.resolvedAt)
            .sort((a, b) => (b.triggeredAt || '').localeCompare(a.triggeredAt || ''));

          setAlerts(active);
          setLoading(false);
        });
      },
    );

    return () => {
      if (primaryUnsub) primaryUnsub();
      if (fallbackUnsub) fallbackUnsub();
    };
  }, [tenantId]);

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;

  return { alerts, unacknowledgedCount, loading };
}
